import { Link } from 'react-router-dom';
import { forwardRef } from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * 화면 액션 버튼 **공통 규격** — 높이·모서리·글자 크기를 여기서만 정한다.
 *
 * 화면마다 따로 클래스를 적으면 같은 자리에 놓인 버튼끼리도 높이와 모서리가 어긋난다
 * (실제로 방 오류 화면에서 [새 방 만들기]와 [홈으로]의 크기가 달랐다).
 * 새 버튼이 필요하면 클래스를 새로 쓰지 말고 여기에 변형을 추가할 것.
 *
 * 폭은 버튼이 정하지 않는다 — 부모가 정한다.
 * 세로로 쌓이는 화면은 `ActionStack`으로 감싸면 폭이 자동으로 맞는다.
 */

const BASE =
  'flex h-12 w-full items-center justify-center gap-1.5 rounded-2xl px-5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40';

const PRIMARY = `${BASE} bg-gold text-night hover:bg-gold-deep`;
const SECONDARY = `${BASE} border border-white/15 bg-white/5 font-medium text-moon-dim hover:border-gold/50 hover:bg-gold/10 hover:text-moon`;

/** 세로로 쌓이는 액션 버튼 묶음 — 가운데 정렬 화면에서 폭을 통일한다 */
export function ActionStack({ children }: { children: ReactNode }) {
  return <div className="flex w-full max-w-xs flex-col gap-2">{children}</div>;
}

/**
 * ref를 넘길 수 있다 — 뽑기를 마친 뒤 이 버튼을 화면 가운데로 데려오는 데 쓴다
 * (features/reading/followScroll.ts). React 18에서는 ref가 props로 넘어가지 않아 forwardRef가 필요하다.
 */
export const PrimaryButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  function PrimaryButton({ className = '', ...props }, ref) {
    return <button ref={ref} {...props} className={`${PRIMARY} ${className}`} />;
  },
);

export function SecondaryButton({
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...props} className={`${SECONDARY} ${className}`} />;
}

export function PrimaryLink({
  to,
  className = '',
  children,
  ...props
}: { to: string; className?: string; children: ReactNode } & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
>) {
  return (
    <Link to={to} {...props} className={`${PRIMARY} ${className}`}>
      {children}
    </Link>
  );
}

/** 앱 밖 주소(토스 앱 링크 등)로 가는 주 버튼 — 라우터 `Link`가 아니라 평범한 `<a href>` */
export function PrimaryAnchor({
  className = '',
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <a {...props} className={`${PRIMARY} ${className}`} />;
}

export function SecondaryLink({
  to,
  className = '',
  children,
  ...props
}: { to: string; className?: string; children: ReactNode } & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
>) {
  return (
    <Link to={to} {...props} className={`${SECONDARY} ${className}`}>
      {children}
    </Link>
  );
}
